export type ContentType = "landing" | "course" | "event" | "qa";
export type BannerSection = "top" | "floating";

export const CONTENT_TYPES: ContentType[] = ["landing", "course", "event", "qa"];

export const CONTENT_TYPE_LABELS: Record<ContentType, string> = {
  landing: "لندینگ",
  course: "دوره‌ها",
  event: "رویدادها",
  qa: "سوال و جواب",
};

export const CONTENT_ENDPOINTS: Record<ContentType, string> = {
  landing: "/admin/banner-content-landing/",
  course: "/admin/banner-content-course/",
  event: "/admin/banner-content-event/",
  qa: "/admin/banner-content-qa/",
};

/**
 * Wire field that holds the related content PK for each floating type.
 * `course` is confirmed by the API spec; `event`/`landing`/`qa` follow the
 * same convention (adjust here if a type uses a different field/slug).
 */
export const CONTENT_FK_FIELD: Record<ContentType, string> = {
  landing: "landing",
  course: "course",
  event: "event",
  qa: "qa",
};

export const TOP_ENDPOINT = "/admin/banner-content-top/";

/**
 * `item_type` passed to the upload signer for all banner images.
 * The uploader only accepts "course" (shared upload bucket) — sending
 * "banner" is rejected with 400 `invalid_choice`, so every banner image is
 * signed with item_type = "course" (matches video/story & the legacy service).
 */
export const UPLOAD_ITEM_TYPE = "course";

export interface BaseBanner {
  uuid: string;
  title: string;
  description: string;
  url: string;
  desktop_image: string | null;
  phone_image: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface TopBanner extends BaseBanner {
  banner_type: ContentType;
  expires_at: string | null;
}

/**
 * Floating banner for a specific content type. `content_id` is the normalized
 * related-content PK (e.g. a Course id); the service maps it to the
 * type-specific wire field via `CONTENT_FK_FIELD`.
 */
export interface ContentBanner extends BaseBanner {
  content_id: number;
}

export type AnyBanner = TopBanner | ContentBanner;

export const isTopBanner = (b: AnyBanner): b is TopBanner => "expires_at" in b;

/** Raw shape returned by the floating endpoints (FK field is dynamic). */
export interface ContentBannerRaw extends BaseBanner {
  [key: string]: unknown;
}

export const normalizeContentBanner =
  (type: ContentType) =>
  (raw: ContentBannerRaw): ContentBanner => ({
    uuid: raw.uuid,
    title: raw.title ?? "",
    description: raw.description ?? "",
    url: raw.url ?? "",
    desktop_image: typeof raw.desktop_image === "string" ? raw.desktop_image : null,
    phone_image: typeof raw.phone_image === "string" ? raw.phone_image : null,
    is_active: Boolean(raw.is_active),
    created_at: raw.created_at ?? "",
    updated_at: raw.updated_at ?? "",
    content_id: Number(raw[CONTENT_FK_FIELD[type]] ?? 0),
  });

export interface CreateTopBannerParams {
  banner_type: ContentType;
  title: string;
  url: string;
  description: string;
  is_active: boolean;
  expires_at: string | null;
  desktopImageFile: File;
  phoneImageFile: File | null;
}

export interface CreateContentBannerParams {
  contentType: ContentType;
  title: string;
  url: string;
  description: string;
  is_active: boolean;
  content_id: number;
  desktopImageFile: File;
  phoneImageFile: File | null;
}

export interface UpdateTopBannerParams {
  uuid: string;
  originalBanner: TopBanner;
  title: string;
  url: string;
  description: string;
  is_active: boolean;
  expires_at: string | null;
  desktopImageFile: File | null;
  phoneImageFile: File | null;
}

export interface UpdateContentBannerParams {
  uuid: string;
  contentType: ContentType;
  originalBanner: ContentBanner;
  title: string;
  url: string;
  description: string;
  is_active: boolean;
  content_id: number;
  desktopImageFile: File | null;
  phoneImageFile: File | null;
}

export interface DeleteTopBannerParams {
  uuid: string;
}

export interface DeleteContentBannerParams {
  uuid: string;
  contentType: ContentType;
}
